/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import locale from 'scripts/utils/locale';
import dayjs from 'dayjs';
import updateLocale from 'dayjs/plugin/updateLocale';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import advanced from 'dayjs/plugin/advancedFormat';

const REGEX_TIMEZONE_OFFSET_FORMAT = /^(.*)([+-])(\d{2}):(\d{2})|(Z)$/;

/**
 * @see https://github.com/iamkun/dayjs/issues/651#issuecomment-763033265
 * decorates dayjs in order to keep the utcOffset of the given date string
 * natively dayjs auto-converts to local time & losing utcOffset info.
 */
const parseZone = (option, dayjsClass, dayjsFactory) => {
    dayjsFactory.parseZone = function(date, format, locale, strict) {
        if (typeof format === 'string') {
            format = { format: format };
        }
        if (typeof date !== 'string') {
            return dayjs(date, format, locale, strict);
        }
        const match = date.match(REGEX_TIMEZONE_OFFSET_FORMAT);
        if (match === null) {
            return dayjs();
        }
        if (match[0] === 'Z') {
            return dayjs(
                date,
                Object.assign(
                    {
                        utc: true,
                    },
                    format,
                ),
                locale,
                strict,
            );
        }
        const [, dateTime, sign, tzHour, tzMinute] = match;
        const uOffset = parseInt(tzHour, 10) * 60 + parseInt(tzMinute, 10);
        const offset = sign === '+' ? uOffset : -uOffset;

        return dayjs(
            dateTime,
            Object.assign(
                {
                    $offset: offset,
                },
                format,
            ),
            locale,
            strict,
        );
    };
};

dayjs.extend(updateLocale);
dayjs.extend(timezone);
dayjs.extend(utc);
dayjs.extend(advanced);

dayjs.extend(parseZone);

dayjs.updateLocale('en', {
    relativeTime: {
        future: 'in %s',
        past: '%s ago',
        s: 'seconds',
        m: '1 minute',
        mm: '%d minutes',
        h: '1 hour',
        hh: '%d hours',
        d: '1 day',
        dd: '%d days',
        M: '1 month',
        MM: '%d months',
        y: '1 year',
        yy: '%d years',
    },
});

export default {
    getTimezone,
    parseDate,
    parseUTCDate,
    parseTimestamp,
    valueOf,
    duration,
    timeAgo,
    formatDate,
    toDate,
    isInProgress,
    toLocal,
    parseDateObj,
    timeLeft,
    getFutureYearsFromNow,
    getPastYearsRange,
    getMonthsNames,
    getMonthsNumbers,
    getMonthDates,
    timeDiff,
    timeDiffPreciseInHours,
    getTimeDiffFromNightInStr,
    isToday,
    getCurrentYear,
    getMinimumDate,
    getMaximumDate,
    addHours,
    copyDate,
    getTimeWithMeridian,
};

function getTimezone() {
    return dayjs.tz(dayjs(), dayjs.tz.guess());
}

function parseDate(date) {
    date = +date ? +date : date;
    return dayjs(date).locale(locale.getLocale());
}

function parseUTCDate(date) {
    return dayjs.utc(date).locale(locale.getLocale());
}

// returns dayjs Date object from dayjs timestamp
function parseTimestamp(timestamp, isMilliseconds) {
    return (isMilliseconds ? dayjs(timestamp) : dayjs.unix(timestamp)).locale(
        locale.getLocale(),
    );
}

// returns current date
function valueOf(date) {
    return dayjs(date).valueOf();
}

// returns duration in seconds formatted as hh:mm:ss
function duration(seconds) {
    var secondsInMinute = 60;
    var pad = (num) => {
        return num < 10 ? ('0' + num).slice(-2) : num;
    };
    var minutes = Math.floor(seconds / secondsInMinute);
    seconds = seconds % secondsInMinute;
    var hours = Math.floor(minutes / secondsInMinute);
    minutes = minutes % secondsInMinute;
    var result = '';
    if (hours) {
        result += pad(hours) + ':';
    }
    result += (result ? pad(minutes) : minutes) + ':';
    result += pad(seconds);
    return result;
}

// returns time ago
function timeAgo(date) {
    return dayjs(date).fromNow();
}

function formatDate(date, format) {
    date = _.isFunction(date) ? date() : date;
    format = _.isFunction(format) ? format() : format;
    return this.toLocal(date).format(format || 'DD MMM YYYY');
}

function toDate(date) {
    return dayjs(date).toDate();
}

function isInProgress(start, end) {
    var now = new Date().valueOf();
    var startDate = this.valueOf(start);
    var endDate = this.valueOf(end);
    return startDate < now && now < endDate;
}

function toLocal(date) {
    return dayjs(date).local();
}

function parseDateObj(obj) {
    if (obj.timezone) {
        return obj.utc + obj.timezone;
    } else {
        return obj.utc + 'Z';
    }
}

function timeLeft(date) {
    var now = dayjs(new Date()).valueOf();
    var start = dayjs(date);
    var duration = dayjs.duration(start - now, 'milliseconds');
    return {
        days: duration.days(),
        hours: duration.hours(),
        minutes: duration.minutes(),
        total: now - start,
    };
}

function getFutureYearsFromNow(yearsFromNow) {
    var currentYear = new Date().getFullYear();
    var result = [currentYear];
    if (yearsFromNow) {
        for (var i = 0; i < yearsFromNow; i += 1) {
            currentYear += 1;
            result.push(currentYear);
        }
    }
    return result;
}

function getPastYearsRange(startYear, stopYear) {
    var currentYear = new Date().getFullYear();
    var result = [];
    if (startYear && stopYear) {
        for (
            var i = currentYear - startYear, last = currentYear - stopYear;
            i <= last;
            i += 1
        ) {
            result.push(i);
        }
    }
    return result;
}

function getMonthsNames(startMonth, stopMonth) {
    var monthsNames = dayjs.months();
    var result = [];
    startMonth = startMonth || 0;
    stopMonth = stopMonth || 11;
    for (var i = startMonth; i <= stopMonth; i += 1) {
        result.push({
            number: i + 1,
            name: monthsNames[i],
        });
    }
    return result;
}

function getMonthsNumbers() {
    var result = [];
    for (var i = 1, len = 12; i <= len; i += 1) {
        result.push(i);
    }
    return result;
}

function getMonthDates(year, month, stopDate) {
    var result = [];
    if (year) {
        month = month || 0;
        let startDate = dayjs([year, month, 0, 0, 0, 0]).toDate();
        let monthEndDay =
            stopDate ||
            dayjs(startDate)
                .endOf('month')
                .date();
        for (var i = 1; i <= monthEndDay; i += 1) {
            result.push(i);
        }
    }
    return result;
}

function timeDiff(startDate, endDate, period) {
    startDate = dayjs(startDate);
    endDate = dayjs(endDate);
    if (period) {
        return endDate.diff(startDate, period);
    } else {
        return endDate.diff(startDate);
    }
}

function timeDiffPreciseInHours(startDate, endDate) {
    return dayjs.duration(timeDiff(startDate, endDate)).asHours();
}

function getTimeDiffFromNightInStr(timeStr) {
    return dayjs.parseZone(timeStr).format('x');
}

function isToday(date) {
    var now = dayjs();
    date = dayjs(date);
    return date.isSame(now, 'day');
}

function getCurrentYear() {
    return dayjs().year();
}

function convertDatesToNumbers(dates) {
    return dates.map((date) => new Date(date).getTime());
}

function getMinimumDate(dates) {
    let minimumDateNumber = 11111111111111111111;
    const convertedDatesToNumbers = convertDatesToNumbers(dates);
    for (let i = 0; i < convertedDatesToNumbers.length; i++) {
        if (convertedDatesToNumbers[i] < minimumDateNumber) {
            minimumDateNumber = convertedDatesToNumbers[i];
        }
    }
    const resultDate =
        dates[convertedDatesToNumbers.indexOf(minimumDateNumber)];
    return resultDate;
}

function getMaximumDate(dates) {
    let maximumDateNumber = 0;
    const convertedDatesToNumbers = convertDatesToNumbers(dates);
    for (let i = 0; i < convertedDatesToNumbers.length; i++) {
        if (convertedDatesToNumbers[i] > maximumDateNumber) {
            maximumDateNumber = convertedDatesToNumbers[i];
        }
    }
    const resultDate =
        dates[convertedDatesToNumbers.indexOf(maximumDateNumber)];
    return resultDate;
}

function addHours(date, hours) {
    const hoursInMiliseconds = hours * 60 * 60 * 1000;
    date.setTime(date.getTime() + hoursInMiliseconds);
    return date;
}

function copyDate(date) {
    return new Date(date.getTime());
}

function getTimeWithMeridian(date) {
    return dayjs(date).format('h:mm A');
}
